import type { IAccount } from "@/modules/account/common/types";
import { useLazyGetCardHolderQuery } from "@/modules/card/services";
import { CardTransferKeys, TransferKeys } from "@/modules/transaction/common/enum";
import { getAccountsFilteredList } from "@/modules/transaction/helpers";
import { useGetBeneficiaryListQuery } from "@/modules/transaction/services";
import { BeneficiaryType, Currencies, ErrorMessages } from "@/shared/common/enums";
import type { IObject } from "@/shared/common/types";
import { formatCardNumber, isValidCard, removeAllSpacesAndDashes, trimAll } from "@/shared/helpers";
import { debounce } from "@/shared/utils";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";

interface UseCardTransferParams {
  accountsList: IAccount[];
  commissionFieldKeys: string[];
  accountFilters?: Currencies[];
  setIsCardNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const useCardTransfer = ({
  accountsList,
  commissionFieldKeys,
  accountFilters = [Currencies.AMD],
  setIsCardNumberInvalid,
}: UseCardTransferParams) => {

  const {
    setValue,
    setError,
    clearErrors,
    watch,
    errors = {},
    reset
  } = useFormProps();

  const [holder, setHolder] = useState<string>("");
  const [isBenSaved, setIsBenSaved] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [cardInputValue, setCardInputValue] = useState<string>("");
  const lastCalledCardRef = useRef<string>("");

  const {
    data: beneficiaryList = [],
    refetch,
  } = useGetBeneficiaryListQuery(
    BeneficiaryType.Card,
    { refetchOnMountOrArgChange: true },
  );
  const [getCardHolder, { isFetching }] = useLazyGetCardHolderQuery();

  const values = watch?.() || {};

  const beneficiaryCardsList = useMemo(() => {
    return beneficiaryList.map((item, index) => ({
      value: item.number,
      label: formatCardNumber({ card: item.number }),
      meta: {
        name: item.name,
        title: item.name,
        index
      },
    }));
  }, [beneficiaryList]);

  const accountsFilteredList = useMemo(() => {
    return getAccountsFilteredList({
      accountsList,
      accountFilters
    });
  }, [accountsList, accountFilters]);

  const getCardInfo = (card: string) => {
    if (!isValidCard(card)) return;
    setIsCardNumberInvalid(false);
    getCardHolder(trimAll(card))
      .unwrap()
      .then((holder) => {
        setHolder(holder.embossedName || "");
        clearErrors?.(CardTransferKeys.CreditCardNumber);
      })
      .catch(() => {
        setHolder("");
        setIsCardNumberInvalid(true);
        setError?.(
          CardTransferKeys.CreditCardNumber,
          { message: ErrorMessages.UnknownCardHolder }
        );
      });
  };

  const handleClearCardNumber = () => {
    setValue?.(
      CardTransferKeys.CreditCardNumber,
      "",
      { shouldValidate: true }
    );
    setCardInputValue("");
    setHolder("");
    setIsCardNumberInvalid(false);
  };

  const handleChangeBeneficiaryCard = useMemo(
    () => debounce((value: string) => {
      const raw = removeAllSpacesAndDashes(value || "");
      if (!raw || !isValidCard(value)) {
        setHolder("");
        lastCalledCardRef.current = raw;
        return;
      }
      if (raw !== lastCalledCardRef.current) {
        getCardInfo(value);
      }
      lastCalledCardRef.current = raw;
    }, 700),
    [],
  );

  const handleChangeCardInput = (value?: string) => {
    setIsCardNumberInvalid(false);
    if (!value) {
      setValue?.(
        CardTransferKeys.CreditCardNumber,
        "",
        { shouldValidate: true }
      );
      setHolder("");
    }
    const raw = removeAllSpacesAndDashes(value || "");
    const formatted = formatCardNumber({ card: raw });
    setCardInputValue(formatted);
  };

  const handleClickTarget = (target: SingleValue<TOption>) => {
    const { currency } = target?.meta as IAccount;
    if (!!values.currency && values.currency !== currency) {
      setValue(TransferKeys.Amount, "");
    }
    setValue(TransferKeys.Currency, currency);
  };

  const creditCardNumber = watch?.(CardTransferKeys.CreditCardNumber);

  const changes = watch?.(commissionFieldKeys).toString();

  const isSaveBeneficiaryEnabled = useMemo(() => {
    return !!(values.creditCardNumber
      && !errors.creditCardNumber
      && isValidCard(values.creditCardNumber)
      && !isFetching
      && !beneficiaryCardsList?.find(({ value }) => value === trimAll(values.creditCardNumber)));
  }, [creditCardNumber, beneficiaryCardsList, isFetching, errors]);

  const fieldChanges = useMemo(() => {
    const fieldValues: IObject = {};
    commissionFieldKeys.forEach((fieldKey: string) => {
      fieldValues[fieldKey] = trimAll(`${watch?.(fieldKey)}`);
    });
    return fieldValues;
  }, [changes]);

  const canGetCommission = useMemo(() => {
    return !!(
      fieldChanges.debitAccount &&
      fieldChanges.creditCardNumber &&
      isValidCard(values.creditCardNumber) &&
      fieldChanges.amount
    );
  }, [fieldChanges]);

  useEffect(() => {
    return () => {
      setHolder("");
    };
  }, []);

  return {
    values,
    errors,
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
    holder,
    isBenSaved,
    setIsBenSaved,
    isOpen,
    setIsOpen,
    cardInputValue,
    setCardInputValue,
    handleChangeCardInput,
    refetch,
    beneficiaryCardsList,
    accountsFilteredList,
    isSaveBeneficiaryEnabled,
    fieldChanges,
    canGetCommission,
    creditCardNumber,
    handleChangeBeneficiaryCard,
    handleClickTarget,
    handleClearCardNumber,
  };
};
